import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { fetchInvoiceForOrderAdmin, type InvoiceForOrder } from '@/lib/invoiceApi';
import { getPaymentStatus } from '@/lib/paymentStatus';

function formatMoney(amount: number, currency: string): string {
  if (currency === 'BDT') return `৳${amount.toLocaleString()}`;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

interface InvoiceCardProps {
  orderId: string;
  orderStatus: string;
  paymentMethod: string | null;
}

export function InvoiceCard({ orderId, orderStatus, paymentMethod }: InvoiceCardProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<InvoiceForOrder | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchInvoiceForOrderAdmin(orderId).then((result) => {
      if (!cancelled) {
        setData(result);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (loading) {
    return (
      <Card>
        <CardHeader title="Invoice" icon={<FileText className="w-4 h-4" />} />
        <CardBody><Spinner /></CardBody>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader title="Invoice" icon={<FileText className="w-4 h-4" />} />
        <CardBody>
          <EmptyState title="No invoice yet" hint="Orders placed before auto-invoicing was enabled don't have one" />
        </CardBody>
      </Card>
    );
  }

  const paymentStatus = getPaymentStatus(orderStatus, paymentMethod);

  return (
    <Card>
      <CardHeader
        title="Invoice"
        subtitle={`${data.request.doc_number ?? 'DRAFT'} · ${formatMoney(data.request.total_amount, data.request.currency)}`}
        icon={<FileText className="w-4 h-4" />}
      />
      <CardBody className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge tone={paymentStatus.tone}>{paymentStatus.label}</Badge>
          <Badge tone={data.status === 'sent' ? 'success' : 'neutral'}>{data.status}</Badge>
        </div>
        <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/orders/${orderId}/invoice`)}>
          <ExternalLink className="w-3.5 h-3.5" /> View / Print Invoice
        </Button>
      </CardBody>
    </Card>
  );
}
